import React from "react"
import PropTypes from "prop-types"
import { Table, Card, CardBody } from "reactstrap"
import IconButton from "./IconButton"

const SimpleTableContainer = ({
    title,
    data = [],
    columns = [],
    loading = false,
    emptyMessage = "No data available",
    emptyIcon = "bx bx-info-circle",
    className = "",
    tableClassName = "table-centered table-nowrap mb-0",
    showActions = true,
    customActions,
    enableInlineEdit = false,
    editingId = null,
    editValue = "",
    onEdit,
    onSave,
    onCancel,
    onDelete,
    onEditValueChange,
    editableField = "value",
    headerActions,
}) => {
    const handleEdit = (item) => {
        if (onEdit) {
            onEdit(item)
        }
    }

    const handleSave = (item) => {
        if (onSave) {
            onSave(item)
        }
    }

    const handleCancel = () => {
        if (onCancel) {
            onCancel()
        }
    }

    const handleDelete = (item) => {
        if (onDelete) {
            onDelete(item)
        }
    }

    const renderCell = (item, column, index) => {
        // Handle inline editing for specific field
        if (enableInlineEdit && editingId === item.id && column.key === editableField) {
            return (
                <input
                    type="text"
                    value={editValue}
                    onChange={(e) => onEditValueChange && onEditValueChange(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter") {
                            handleSave(item)
                        }
                    }}
                    className="form-control form-control-sm"
                    autoFocus
                />
            )
        }

        // Use custom render function if provided
        if (column.render && typeof column.render === "function") {
            return column.render(item, index)
        }

        // Handle nested object access (e.g., "user.name")
        const value = column.key?.split('.').reduce((obj, key) => obj?.[key], item)

        // Apply text truncation if specified
        if (column.truncate && value && value.length > column.truncate) {
            return (
                <span title={value}>
                    {value.substring(0, column.truncate)}...
                </span>
            )
        }

        return value || "---"
    }

    const renderActions = (item, index) => {
        if (customActions) {
            return customActions(item, index)
        }

        if (!showActions) {
            return null
        }

        if (enableInlineEdit && editingId === item.id) {
            return (
                <div className="d-flex justify-content-center gap-1">
                    <IconButton.Save
                        onClick={() => handleSave(item)}
                    />
                    <IconButton.Cancel
                        onClick={handleCancel}
                    />
                </div>
            )
        }

        return (
            <div className="d-flex justify-content-center gap-1">
                {onEdit && (
                    <IconButton.Edit
                        onClick={() => handleEdit(item)}
                        disabled={editingId !== null}
                    />
                )}
                {onDelete && (
                    <IconButton.Delete
                        onClick={() => handleDelete(item)}
                        disabled={editingId !== null}
                    />
                )}
            </div>
        )
    }

    return (
        <Card className={className}>
            <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">{title}</h5>
                    {headerActions}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!loading && data.length === 0 && (
                    <div className="text-center py-4">
                        <i className={`${emptyIcon} display-1 text-muted`}></i>
                        <p className="text-muted mt-3">{emptyMessage}</p>
                    </div>
                )}

                {/* Table */}
                {!loading && data.length > 0 && (
                    <div className="table-responsive">
                        <Table className={tableClassName}>
                            <thead className="table-light">
                                <tr>
                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            style={{
                                                width: column.width,
                                                textAlign: column.align || "left",
                                            }}
                                        >
                                            {column.label}
                                        </th>
                                    ))}
                                    {showActions && (
                                        <th className="text-center">
                                            Actions
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, index) => (
                                    <tr
                                        key={item.id || index}
                                        className="align-middle"
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                style={{ textAlign: column.align || "left" }}
                                            >
                                                {renderCell(item, column, index)}
                                            </td>
                                        ))}
                                        {showActions && (
                                            <td className="text-center">
                                                {renderActions(item, index)}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
        </Card>
    )
}

SimpleTableContainer.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.array,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            width: PropTypes.string,
            align: PropTypes.oneOf(["left", "center", "right"]),
            render: PropTypes.func,
            truncate: PropTypes.number,
        })
    ),
    loading: PropTypes.bool,
    emptyMessage: PropTypes.string,
    emptyIcon: PropTypes.string,
    className: PropTypes.string,
    tableClassName: PropTypes.string,
    showActions: PropTypes.bool,
    customActions: PropTypes.func,
    enableInlineEdit: PropTypes.bool,
    editingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    editValue: PropTypes.string,
    onEdit: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onDelete: PropTypes.func,
    onEditValueChange: PropTypes.func,
    editableField: PropTypes.string,
    headerActions: PropTypes.node,
}

export default SimpleTableContainer